import {
  Component, OnInit, OnDestroy, inject, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subscription } from 'rxjs';
import {
  Asset, bookingDurationHours, DEFAULT_PRICING, formatEuro,
  hourlyRateForDate, isEveOfHoliday, isHolidayDate, minHoursForSpace,
  quoteRentalSubtotal, ScheduleException,
} from '../../models/asset.model';
import { Booking, isCalendarBlockingStatus } from '../../models/booking.model';
import { BookingService } from '../../services/booking.service';
import { AssetService } from '../../services/asset.service';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import {
  clampWeekStart, initialBookableWeekStart, isBookableDay, isMiniCalDayDisabled,
  bookableMaxDay, bookableMinDay,
} from '../../shared/schedule/booking-window.utils';
import { MONTH_NAMES, SLOT_ROW_HEIGHT, WEEKDAY_LABELS_SHORT } from '../../shared/schedule/schedule.constants';
import { dayHoursForDate } from '../../shared/schedule/schedule-pricing.utils';
import {
  dayOfWeekMon0, formatWeekRange, mondayOf, parseCloseHour, parseHour, slotTime, startOfDay, toDateStr,
  weekDatesFromMonday,
} from '../../shared/schedule/schedule.utils';
import {
  clearPendingBookingSelection,
  peekPendingBookingSelection,
  savePendingBookingSelection,
} from '../../shared/booking/booking-selection-persistence';
import { buildVisibleSlots, GridSlot, slotFractions } from '../../shared/schedule/slot-grid.utils';
import { heatmapDotForDay } from '../../shared/schedule/grid-builder';
import {
  applyBlockingBookingsToDay,
  applyFixedBlocksToDay,
  applySeasonalBlocksToDay,
} from '../../shared/schedule/schedule-occupancy.utils';

/** Merged occupied span on the public grid. */
interface BusyRun {
  key: string;
  startIdx: number;
  spanSlots: number;
  /** 'block' = recurring owner block; 'booking' = customer booking. */
  kind: 'block' | 'booking';
}

interface GridColumn {
  date: Date;
  dateStr: string;
  freeMask: boolean[];
  busyRuns: BusyRun[];
  pastMask: boolean[];
  /** true when the slot is occupied solely by a cancelableOnFullDay recurring block. */
  cancelableMask: boolean[];
  /** Operating hours available for full-day booking: (free + cancelable) * 0.5. */
  fullDayHours: number;
  /** Day can be booked as full-day: bookable + no hard blocks/bookings + enough hours. */
  fullDayEligible: boolean;
}

/** Statuses that come from recurring owner blocks (not customer bookings). */
const BLOCK_STATUSES = new Set(['FIXED', 'SEASONAL']);

/** Colors for the two types of occupied runs. */
const BOOKING_COLOR = '#94A3B8';                // steel-gray  — customer booking
const BLOCK_COLOR   = 'rgba(11,53,88,0.22)';   // midnight-indigo tint — owner block

interface DayDot {
  date: Date;
  dateStr: string;
  inMonth: boolean;
  disabled: boolean;
  holidayType: 'none' | 'national' | 'custom';
  dot: 'green' | 'gray' | 'red' | 'none';
}

interface CalMonth {
  year: number;
  month: number;
  label: string;
  weeks: DayDot[][];
}

@Component({
  selector: 'app-user-booking',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './user-booking.component.html',
})
export class UserBookingComponent implements OnInit, OnDestroy {
  private route          = inject(ActivatedRoute);
  private router         = inject(Router);
  private auth           = inject(Auth);
  private assetService   = inject(AssetService);
  private spaceRoutes    = inject(SpacePublicRouteService);
  private bookingService = inject(BookingService);

  readonly ROW_HEIGHT  = SLOT_ROW_HEIGHT;
  readonly DAYS_SHORT  = [...WEEKDAY_LABELS_SHORT];
  readonly MONTH_NAMES = MONTH_NAMES;

  weekdayLabel(date: Date): string {
    return WEEKDAY_LABELS_SHORT[dayOfWeekMon0(date)];
  }

  readonly spaceId    = signal<string>('');
  readonly space      = signal<Asset|null>(null);
  readonly bookings   = signal<Booking[]>([]);
  readonly exceptions = signal<ScheduleException[]>([]);
  readonly loading    = signal(true);

  readonly weekStart  = signal<Date>(initialBookableWeekStart());

  /**
   * Number of months from the current month to display in the mini-calendar.
   * 0 = current month, 1 = next, -1 = previous, etc. Bound to the prev/next
   * chevrons + "Hoy" pill in the sidebar card.
   */
  readonly miniMonthOffset = signal(0);

  /** Monday of the week that contains the first bookable day. */
  readonly minWeekStart = computed(() => mondayOf(bookableMinDay()));
  /** Monday of the week that contains the last bookable day. */
  readonly maxWeekStart = computed(() => mondayOf(bookableMaxDay()));
  /** Furthest mini-calendar month offset (0 = current month). */
  readonly maxMiniMonthOffset = computed(() => {
    const today = startOfDay(new Date());
    const max   = bookableMaxDay();
    return (max.getFullYear() - today.getFullYear()) * 12 + (max.getMonth() - today.getMonth());
  });

  /** Selection: a single contiguous range on one date. */
  readonly selectedDate  = signal<string>('');
  readonly selectedStart = signal<string>('');
  readonly selectedEnd   = signal<string>('');

  readonly submitting = signal(false);
  readonly error      = signal<string | null>(null);
  readonly showCleaningFeeModal = signal(false);
  readonly hasCleaning = signal(false);

  private subs: Subscription[] = [];

  // ── Read-only catalog of slots (visible operating hours) ───────────────────
  readonly slots = computed<GridSlot[]>(() => {
    const space = this.space();
    if (!space) return [];
    return buildVisibleSlots(space.openingTime, space.closingTime);
  });

  readonly weekDates = computed(() => weekDatesFromMonday(this.weekStart()));

  /** Total grid body height in px — schedule-step parity. */
  readonly gridH = computed(() => this.slots().length * this.ROW_HEIGHT);

  // ── 7-day availability grid ────────────────────────────────────────────────
  readonly grid = computed<GridColumn[]>(() => {
    const space  = this.space();
    const slots  = this.slots();
    if (!space || slots.length === 0) return [];
    const exceptions = this.exceptions();
    const weekDateStrs = this.weekDates().map(toDateStr);
    const weekBookings = this.bookings().filter(b =>
      weekDateStrs.includes(b.date) && isCalendarBlockingStatus(b.status),
    );
    const fracs = slotFractions(slots);

    return this.weekDates().map((date, ci) => {
      const dateStr  = weekDateStrs[ci];
      const { open: openStr, close: closeStr } = dayHoursForDate(space, date, dateStr);
      const open  = parseHour(openStr);
      const close = parseCloseHour(closeStr, openStr);

      const bookable = isBookableDay(date);

      // Masks — cancelableMask tracks slots occupied only by cancelableOnFullDay blocks.
      const freeMask:       boolean[]                                = slots.map(() => bookable);
      const busyMeta:       ({label: string; status: string}|null)[] = slots.map(() => null);
      const pastMask:       boolean[]                                = slots.map(() => !bookable);
      const cancelableMask: boolean[]                                = slots.map(() => false);

      if (!bookable) {
        // Past / non-bookable: show owner blocks visually (pastMask stripes render on top).
        const p = { space, date, dateStr, slots, slotFractions: fracs, open, close, exceptions, freeMask, busyMeta, cancelableMask };
        applyFixedBlocksToDay({ ...p, anonymize: false });
        applySeasonalBlocksToDay({ ...p, anonymize: false });
        // No booking runs on past days (privacy + irrelevant to the guest).
      } else {
        // Slots outside the day's operating hours are not bookable.
        for (let si = 0; si < fracs.length; si++) {
          const frac = fracs[si];
          if (frac < open || frac >= close) {
            freeMask[si] = false;
            pastMask[si] = true;
          }
        }

        const p = { space, date, dateStr, slots, slotFractions: fracs, open, close, exceptions, freeMask, busyMeta, cancelableMask };

        // Recurring blocks: real status preserved; cancelableMask populated.
        applyFixedBlocksToDay({ ...p, anonymize: false });
        applySeasonalBlocksToDay({ ...p, anonymize: false });

        // Bookings: anonymized (hides guest names / status details).
        applyBlockingBookingsToDay({
          bookings: weekBookings,
          dateStr, slots, open, close, freeMask, busyMeta,
          anonymize: true,
        });
      }

      // ── Full-day eligibility ─────────────────────────────────────────────────
      // Eligible when: bookable + no hard-occupied operating slot + enough hours
      const fdThreshold = space.pricing?.fullDayThreshold ?? DEFAULT_PRICING.fullDayThreshold;
      let fullDayHours    = 0;
      let hasHardOccupied = false;
      if (bookable) {
        for (let si = 0; si < fracs.length; si++) {
          const f = fracs[si];
          if (f < open || f >= close) continue;
          if (freeMask[si] || cancelableMask[si]) { fullDayHours += 0.5; }
          else                                    { hasHardOccupied = true; }
        }
      }
      const fullDayEligible = bookable && !hasHardOccupied && fullDayHours >= fdThreshold;

      // Merge consecutive occupied slots, but split at block/booking boundaries
      // so each type can be rendered with its own color.
      const kindOf = (meta: { status: string } | null): 'block' | 'booking' =>
        meta && BLOCK_STATUSES.has(meta.status) ? 'block' : 'booking';

      const busyRuns: BusyRun[] = [];
      let i = 0;
      while (i < busyMeta.length) {
        if (busyMeta[i]) {
          const kind = kindOf(busyMeta[i]);
          let j = i + 1;
          while (j < busyMeta.length && !!busyMeta[j] && kindOf(busyMeta[j]) === kind) {
            j++;
          }
          busyRuns.push({ key: `${dateStr}:${i}`, startIdx: i, spanSlots: j - i, kind });
          i = j;
        } else {
          i++;
        }
      }

      return { date, dateStr, freeMask, busyRuns, pastMask, cancelableMask, fullDayHours, fullDayEligible };
    });
  });

  // ── Mini-calendar with heatmap (offset-driven for prev/next navigation) ────
  readonly miniCalendar = computed<CalMonth>(() => {
    const today = new Date();
    const base  = new Date(today.getFullYear(), today.getMonth() + this.miniMonthOffset(), 1);
    return this.#buildMonth(base);
  });

  // ── Selection / price helpers ──────────────────────────────────────────────
  /** Venue record (alias for product copy). */
  readonly venue = computed(() => this.space());

  readonly hasSelection = computed(() =>
    !!this.selectedDate() && !!this.selectedStart() && !!this.selectedEnd()
  );

  /** Half-hour slots in the current selection (grid-native duration). */
  readonly selectedSlotCount = computed(() => {
    if (!this.hasSelection()) return 0;
    const slots    = this.slots();
    const startIdx = slots.findIndex(s => slotTime(s.hour, s.half) === this.selectedStart());
    const endIdx   = slots.findIndex(s => slotTime(s.hour, s.half) === this.selectedEnd());
    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
      return endIdx - startIdx;
    }
    return Math.round(bookingDurationHours(this.selectedStart(), this.selectedEnd()) * 2);
  });

  /** Duration of the current slot selection (hours, 30-min granularity). */
  readonly selectedHours = computed(() => this.selectedSlotCount() * 0.5);

  readonly minHours = computed(() => {
    const v = this.venue();
    return v ? minHoursForSpace(v) : DEFAULT_PRICING.minHours;
  });

  readonly isMinHoursMet = computed(() =>
    this.selectedHours() === 0 || this.selectedHours() >= this.minHours(),
  );

  /** €/h for the selected calendar day (weekday, eve, or holiday). */
  readonly hourlyRate = computed(() => {
    const v = this.venue();
    const d = this.selectedDate();
    if (!v || !d) return 0;
    return hourlyRateForDate(v, d);
  });

  /** Short label when a festivo / víspera rate applies. */
  readonly rateLabel = computed(() => {
    const v = this.venue();
    const d = this.selectedDate();
    if (!v || !d) return '';
    if (isHolidayDate(d, v)) return 'Tarifa festivo';
    if (isEveOfHoliday(d, v)) return 'Tarifa víspera de festivo';
    return '';
  });

  /** Rental total (hourly rate × selected 30-min slots); excludes deposit and cleaning. */
  readonly baseTotalPrice = computed(() => {
    const v = this.venue();
    if (!v || !this.hasSelection()) return 0;
    return quoteRentalSubtotal(v, this.hourlyRate(), this.selectedHours());
  });

  readonly formatPrice = formatEuro;

  /** Manual deposit paid directly to the owner (€). */
  readonly deposit = computed(() => this.venue()?.securityDeposit ?? 0);

  readonly showMinHoursWarning = computed(() =>
    this.selectedHours() > 0 && !this.isMinHoursMet(),
  );

  readonly cleaningFee = computed(() =>
    Math.max(0, this.venue()?.pricing?.cleaningFee ?? 0),
  );

  readonly totalPrice = computed(() => {
    const cleaning = this.hasCleaning() ? this.cleaningFee() : 0;
    return Math.round((this.baseTotalPrice() + cleaning) * 100) / 100;
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    void this.#initFromRoute();
  }

  async #initFromRoute(): Promise<void> {
    const paramMap = this.route.snapshot.paramMap;
    let space = this.spaceRoutes.parseRoute(paramMap)
      ? await this.spaceRoutes.loadSpaceFromRoute(paramMap)
      : null;

    if (!space) {
      space = await this.spaceRoutes.loadPendingBookingSpace();
    }

    if (!space) {
      this.router.navigate(['/404']);
      return;
    }
    if (!space.active) {
      const uid = this.auth.currentUser?.uid;
      if (!uid || space.ownerId !== uid) {
        this.router.navigate(['/404']);
        return;
      }
    }

    const id = space.id!;
    this.spaceId.set(id);
    this.#restorePendingSelection(id);

    // If no prior selection was restored from session, honor the ?date= query
    // param set by the public calendar modal when the user clicks a free day.
    if (!this.selectedDate()) {
      const dateParam = this.route.snapshot.queryParamMap.get('date');
      if (dateParam) {
        const [y, m, d] = dateParam.split('-').map(Number);
        if (y && m && d) {
          this.selectedDate.set(dateParam);
          this.goToWeekOf(new Date(y, m - 1, d));
          const startParam = this.route.snapshot.queryParamMap.get('startTime')?.trim();
          const endParam = this.route.snapshot.queryParamMap.get('endTime')?.trim();
          if (startParam && endParam) {
            this.selectedStart.set(startParam);
            this.selectedEnd.set(endParam);
          }
        }
      }
    }

    this.subs.push(
      this.assetService.watchSpace(id).subscribe({
        next: data => { this.space.set(data); this.loading.set(false); },
        error: () => this.loading.set(false),
      }),
    );

    // Availability + exceptions — routed through BookingService so the
    // read-side concerns (status filter, exception subcollection path,
    // injection context) stay in one place.
    const today = startOfDay(new Date());
    const start = toDateStr(new Date(today.getFullYear(), today.getMonth(), 1));
    const end   = toDateStr(bookableMaxDay());
    this.subs.push(
      this.bookingService.getAvailability(id, { start, end })
        .subscribe(data => this.bookings.set(data)),
    );
    this.subs.push(
      this.bookingService.getExceptions(id, { start, end })
        .subscribe(data => this.exceptions.set(data)),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ── Navigation ────────────────────────────────────────────────────────────
  readonly canGoPrevWeek = computed(() =>
    this.weekStart().getTime() > this.minWeekStart().getTime(),
  );

  readonly canGoNextWeek = computed(() =>
    this.weekStart().getTime() < this.maxWeekStart().getTime(),
  );

  prevWeek(): void {
    if (!this.canGoPrevWeek()) return;
    const d = new Date(this.weekStart());
    d.setDate(d.getDate() - 7);
    this.weekStart.set(d);
    this.#ensureMonthVisible(d);
  }
  nextWeek(): void {
    if (!this.canGoNextWeek()) return;
    const d = new Date(this.weekStart());
    d.setDate(d.getDate() + 7);
    this.weekStart.set(d);
    this.#ensureMonthVisible(d);
  }
  prevMiniMonth(): void {
    if (this.miniMonthOffset() <= 0) return;
    this.miniMonthOffset.update(v => v - 1);
  }
  nextMiniMonth(): void {
    if (this.miniMonthOffset() >= this.maxMiniMonthOffset()) return;
    this.miniMonthOffset.update(v => v + 1);
  }
  goToCurrentMonth(): void {
    this.miniMonthOffset.set(0);
    this.weekStart.set(clampWeekStart(mondayOf(new Date())));
  }
  goBack(): void {
    const path = this.router.url.split('?')[0];
    if (path.endsWith('/reservar')) {
      const detailPath = path.slice(0, -'/reservar'.length);
      void this.router.navigateByUrl(detailPath || '/explorar');
      return;
    }

    const id = this.space()?.id ?? this.spaceId();
    if (id) {
      void this.router.navigate(['/espacio', id]);
      return;
    }
    void this.router.navigate(['/explorar']);
  }

  /** True when `date` falls within the week currently shown on the right. */
  isInDisplayedWeek(date: Date): boolean {
    const monday = this.weekStart();
    const d0 = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const m0 = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate()).getTime();
    return d0 >= m0 && d0 < m0 + 7 * 24 * 60 * 60 * 1000;
  }

  // ── Selection ─────────────────────────────────────────────────────────────
  /** Click on a free slot — anchor or extend the current selection. */
  pickSlot(col: GridColumn, slotIdx: number): void {
    if (!isBookableDay(col.date) || !col.freeMask[slotIdx]) return;
    const slots = this.slots();
    const slot  = slots[slotIdx];

    // Starting a fresh selection or switching date.
    if (this.selectedDate() !== col.dateStr || !this.hasSelection()) {
      this.selectedDate.set(col.dateStr);
      this.selectedStart.set(slotTime(slot.hour, slot.half));
      // Default end = +30 min, but only if next slot is also free.
      if (slotIdx + 1 < slots.length && col.freeMask[slotIdx + 1]) {
        const nx = slots[slotIdx + 1];
        this.selectedEnd.set(slotTime(nx.hour, nx.half));
      } else {
        // Otherwise end == start+30 even if it falls outside the window.
        const totalHalves = slot.hour * 2 + slot.half + 1;
        this.selectedEnd.set(slotTime(Math.floor(totalHalves / 2), (totalHalves % 2) as 0|1));
      }
      this.error.set(null);
      return;
    }

    // Extend: ensure contiguous, no busy slot in between.
    const startIdx = slots.findIndex(s => slotTime(s.hour, s.half) === this.selectedStart());
    const [lo, hi] = slotIdx < startIdx ? [slotIdx, startIdx] : [startIdx, slotIdx];
    for (let i = lo; i <= hi; i++) {
      if (!col.freeMask[i]) { this.error.set('El rango incluye un hueco ocupado.'); return; }
    }
    const startSlot = slots[lo];
    const endHalves = slots[hi].hour * 2 + slots[hi].half + 1;
    this.selectedStart.set(slotTime(startSlot.hour, startSlot.half));
    this.selectedEnd.set(slotTime(Math.floor(endHalves / 2), (endHalves % 2) as 0|1));
    this.error.set(null);
  }

  clearSelection(): void {
    this.selectedDate.set(''); this.selectedStart.set(''); this.selectedEnd.set('');
    this.error.set(null);
    this.hasCleaning.set(false);
    clearPendingBookingSelection();
  }

  /**
   * Redirects to checkout draft flow without pre-creating a Firestore booking.
   */
  async confirmBooking(): Promise<void> {
    if (this.submitting() || !this.hasSelection()) return;
    const space = this.space();
    if (!space?.id) {
      this.error.set('Inicia sesión para reservar.');
      return;
    }
    const minStr = toDateStr(bookableMinDay());
    const maxStr = toDateStr(bookableMaxDay());
    if (this.selectedDate() < minStr || this.selectedDate() > maxStr) {
      this.error.set('La reserva debe ser entre mañana y los próximos 6 meses.');
      this.clearSelection();
      return;
    }
    if (!this.isMinHoursMet()) {
      this.error.set(`La reserva mínima es de ${this.minHours()} horas.`);
      return;
    }
    this.submitting.set(true);
    try {
      const userId = this.auth.currentUser?.uid ?? '';
      if (!userId) {
        throw new Error('Inicia sesión para continuar con el pago.');
      }
      savePendingBookingSelection({
        spaceId: space.id,
        date: this.selectedDate(),
        startTime: this.selectedStart(),
        endTime: this.selectedEnd(),
        hasCleaning: this.hasCleaning(),
      });
      this.router.navigate(['/booking/checkout', space.id], {
        queryParams: {
          userId,
          date: this.selectedDate(),
          startTime: this.selectedStart(),
          endTime: this.selectedEnd(),
          totalPrice: this.totalPrice(),
          ownerId: space.ownerId,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear la reserva. Inténtalo de nuevo.';
      this.error.set(message || 'No se pudo crear la reserva. Inténtalo de nuevo.');
    } finally {
      this.submitting.set(false);
    }
  }

  onConfirmBookingClick(): void {
    if (this.submitting() || !this.hasSelection() || !this.isMinHoursMet()) return;
    if (this.cleaningFee() > 0) {
      this.hasCleaning.set(false);
      this.showCleaningFeeModal.set(true);
      return;
    }
    void this.confirmBooking();
  }

  closeCleaningFeeModal(): void {
    this.showCleaningFeeModal.set(false);
  }

  continueWithoutCleaning(): void {
    this.hasCleaning.set(false);
    this.showCleaningFeeModal.set(false);
    void this.confirmBooking();
  }

  acceptCleaningFeeAndContinue(): void {
    this.hasCleaning.set(true);
    this.showCleaningFeeModal.set(false);
    void this.confirmBooking();
  }

  // ── Utilities ─────────────────────────────────────────────────────────────
  isToday(date: Date): boolean {
    const t = new Date();
    return date.getFullYear() === t.getFullYear()
        && date.getMonth()    === t.getMonth()
        && date.getDate()     === t.getDate();
  }
  formatHour(h: number, half: 0|1): string { return slotTime(h, half); }

  readonly bookingColor = BOOKING_COLOR;
  readonly blockColor   = BLOCK_COLOR;

  /** Full-day booking threshold in hours (from space pricing config, default 6 h). */
  readonly fullDayThreshold = computed(() =>
    this.space()?.pricing?.fullDayThreshold ?? DEFAULT_PRICING.fullDayThreshold
  );

  // ── Full-day booking modal ─────────────────────────────────────────────────

  readonly showFullDayModal = signal(false);
  readonly fdModalCol       = signal<GridColumn | null>(null);
  readonly fdModalStart     = signal('');
  readonly fdModalEnd       = signal('');

  /** Start time options for the modal: free-or-cancelable slots in the operating window. */
  readonly fdStartOptions = computed<string[]>(() => {
    const col   = this.fdModalCol();
    const space = this.space();
    if (!col || !space) return [];
    const { open: openStr, close: closeStr } = dayHoursForDate(space, col.date, col.dateStr);
    const openFrac  = parseHour(openStr);
    const closeFrac = parseCloseHour(closeStr, openStr);
    const threshold = this.fullDayThreshold();
    const slots     = this.slots();
    const fracs     = slotFractions(slots);
    return slots
      .map((s, i) => ({ s, i, f: fracs[i] }))
      .filter(({ f, i }) =>
        f >= openFrac &&
        f + threshold <= closeFrac &&          // must leave room for the minimum
        (col.freeMask[i] || col.cancelableMask[i]),
      )
      .map(({ s }) => slotTime(s.hour, s.half));
  });

  /** End time options for the modal: exclusive boundaries ≥ start + threshold. */
  readonly fdEndOptions = computed<string[]>(() => {
    const col       = this.fdModalCol();
    const space     = this.space();
    const startTime = this.fdModalStart();
    if (!col || !space || !startTime) return [];
    const { open: openStr, close: closeStr } = dayHoursForDate(space, col.date, col.dateStr);
    const closeFrac  = parseCloseHour(closeStr, openStr);
    const threshold  = this.fullDayThreshold();
    const startFrac  = parseHour(startTime);
    const slots      = this.slots();
    const fracs      = slotFractions(slots);
    const startIdx   = slots.findIndex(s => slotTime(s.hour, s.half) === startTime);
    if (startIdx === -1) return [];

    const opts: string[] = [];
    for (let i = startIdx; i < slots.length; i++) {
      const f = fracs[i];
      if (f >= closeFrac) break;
      if (!col.freeMask[i] && !col.cancelableMask[i]) break; // hard block ends the range
      const boundaryFrac = f + 0.5; // exclusive end after this slot
      if (boundaryFrac - startFrac >= threshold) {
        const bH = Math.floor(boundaryFrac);
        const bM = Math.round((boundaryFrac - bH) * 60);
        const label = boundaryFrac >= 24
          ? '24:00'
          : `${String(bH).padStart(2, '0')}:${String(bM).padStart(2, '0')}`;
        if (!opts.includes(label)) opts.push(label);
      }
    }
    // Always offer the operating close time as the last option if within range
    const closeLabel = closeFrac >= 24
      ? '24:00'
      : `${String(Math.floor(closeFrac)).padStart(2, '0')}:00`;
    if (closeFrac - startFrac >= threshold && !opts.includes(closeLabel)) {
      opts.push(closeLabel);
    }
    return opts;
  });

  readonly fdModalHours = computed(() => {
    const s = this.fdModalStart();
    const e = this.fdModalEnd();
    if (!s || !e) return 0;
    return bookingDurationHours(s, e);
  });

  readonly fdModalPrice = computed(() => {
    const col   = this.fdModalCol();
    const space = this.space();
    const hours = this.fdModalHours();
    if (!col || !space || hours <= 0) return 0;
    const rate = hourlyRateForDate(space, col.dateStr);
    return quoteRentalSubtotal(space, rate, hours);
  });

  openFullDayModal(col: GridColumn): void {
    if (!col.fullDayEligible) return;
    const space = this.space();
    if (!space) return;

    const { open: openStr, close: closeStr } = dayHoursForDate(space, col.date, col.dateStr);
    const openFrac  = parseHour(openStr);
    const closeFrac = parseCloseHour(closeStr, openStr);
    const slots     = this.slots();
    const fracs     = slotFractions(slots);

    // Default start = first free/cancelable operating slot
    const firstIdx = slots.findIndex((_, i) =>
      fracs[i] >= openFrac && (col.freeMask[i] || col.cancelableMask[i]),
    );
    const defaultStart = firstIdx !== -1
      ? slotTime(slots[firstIdx].hour, slots[firstIdx].half)
      : openStr;

    // Default end = operating close time
    const defaultEnd = closeFrac >= 24
      ? '24:00'
      : `${String(Math.floor(closeFrac)).padStart(2, '0')}:00`;

    this.fdModalCol.set(col);
    this.fdModalStart.set(defaultStart);
    this.fdModalEnd.set(defaultEnd);
    this.showFullDayModal.set(true);
  }

  closeFullDayModal(): void {
    this.showFullDayModal.set(false);
  }

  onFdStartChange(label: string): void {
    this.fdModalStart.set(label);
    // Reset end to last valid option if current end is no longer valid
    const opts = this.fdEndOptions();
    if (opts.length && !opts.includes(this.fdModalEnd())) {
      this.fdModalEnd.set(opts[opts.length - 1]);
    }
  }

  confirmFullDayModal(): void {
    const col   = this.fdModalCol();
    const start = this.fdModalStart();
    const end   = this.fdModalEnd();
    if (!col || !start || !end || this.fdModalHours() < this.fullDayThreshold()) return;

    this.selectedDate.set(col.dateStr);
    this.selectedStart.set(start);
    this.selectedEnd.set(end);
    this.error.set(null);
    this.hasCleaning.set(false);
    this.showFullDayModal.set(false);

    if (this.cleaningFee() > 0) {
      this.showCleaningFeeModal.set(true);
    } else {
      void this.confirmBooking();
    }
  }

  /** Formats a Date as "Lunes, 28 de mayo" for the modal header. */
  formatModalDate(date: Date): string {
    const days   = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
    const months = ['enero','febrero','marzo','abril','mayo','junio',
                    'julio','agosto','septiembre','octubre','noviembre','diciembre'];
    return `${days[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]}`;
  }
  formatWeekRange(): string {
    return formatWeekRange(this.weekDates());
  }
  isSelectedSlot(col: GridColumn, slotIdx: number): boolean {
    if (col.dateStr !== this.selectedDate() || !this.hasSelection()) return false;
    const slots = this.slots();
    const startIdx = slots.findIndex(s => slotTime(s.hour, s.half) === this.selectedStart());
    const endIdx   = slots.findIndex(s => slotTime(s.hour, s.half) === this.selectedEnd());
    const endBound = endIdx === -1 ? slots.length : endIdx;
    return slotIdx >= startIdx && slotIdx < endBound;
  }

  goToWeekOf(date: Date): void {
    if (isMiniCalDayDisabled(date)) return;
    this.weekStart.set(clampWeekStart(mondayOf(date)));
    this.#ensureMonthVisible(this.weekStart());
  }

  #restorePendingSelection(spaceId: string): void {
    const pending = peekPendingBookingSelection(spaceId);
    if (!pending) return;

    this.selectedDate.set(pending.date);
    this.selectedStart.set(pending.startTime);
    this.selectedEnd.set(pending.endTime);
    this.hasCleaning.set(pending.hasCleaning);
    this.error.set(null);

    const [y, m, d] = pending.date.split('-').map(Number);
    if (y && m && d) {
      this.goToWeekOf(new Date(y, m - 1, d));
    }
  }

  #ensureMonthVisible(monday: Date): void {
    const today = new Date();
    const monthsFromToday =
      (monday.getFullYear() - today.getFullYear()) * 12 +
      (monday.getMonth() - today.getMonth());
    if (monthsFromToday !== this.miniMonthOffset()) {
      this.miniMonthOffset.set(monthsFromToday);
    }
  }

  /** Builds a month worth of cells with availability heatmap dots. */
  #buildMonth(today: Date): CalMonth {
    const base = new Date(today.getFullYear(), today.getMonth(), 1);
    const yr   = base.getFullYear();
    const mo   = base.getMonth();
    const daysInMonth = new Date(yr, mo + 1, 0).getDate();
    const firstDow    = (new Date(yr, mo, 1).getDay() + 6) % 7;
    const space       = this.space();
    const customHolidaySet = new Set(space?.customHolidays ?? []);
    const exceptions  = this.exceptions();
    const cells: DayDot[] = [];

    for (let i = 0; i < firstDow; i++) {
      const d = new Date(yr, mo, 1 - (firstDow - i));
      cells.push({
        date: d,
        dateStr: toDateStr(d),
        inMonth: false,
        disabled: isMiniCalDayDisabled(d),
        holidayType: 'none',
        dot: 'none',
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const d       = new Date(yr, mo, day);
      const dateStr = toDateStr(d);
      const holidayType: DayDot['holidayType'] = !space
        ? 'none'
        : customHolidaySet.has(dateStr)
          ? 'custom'
          : isHolidayDate(dateStr, space)
            ? 'national'
            : 'none';

      let dot: DayDot['dot'] = 'none';

      // Skip the heatmap on past days (not bookable) AND on today — a single
      // dot can't truthfully summarise a day where some slots have already
      // passed and others are still free.
      if (space && isBookableDay(d)) {
        dot = heatmapDotForDay({
          space,
          dateStr,
          date: d,
          bookings: this.bookings(),
          exceptions,
        });
      }

      cells.push({
        date: d,
        dateStr,
        inMonth: true,
        disabled: isMiniCalDayDisabled(d),
        holidayType,
        dot,
      });
    }

    while (cells.length % 7 !== 0) {
      const d = new Date(yr, mo + 1, cells.length % 7);
      cells.push({
        date: d,
        dateStr: toDateStr(d),
        inMonth: false,
        disabled: isMiniCalDayDisabled(d),
        holidayType: 'none',
        dot: 'none',
      });
    }

    const weeks: DayDot[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return { year: yr, month: mo, label: `${MONTH_NAMES[mo]} ${yr}`, weeks };
  }
}
